import { useState, useEffect } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  TransitionChild,
} from "@headlessui/react";
import {
  Bars3Icon,
  XMarkIcon,
  UsersIcon,
  ChartPieIcon,
  PhoneIcon,
  CurrencyDollarIcon,
  Cog6ToothIcon,
  BellIcon,
  ClipboardIcon,
} from "@heroicons/react/24/outline";
import { Link, Outlet } from "react-router-dom";
import logo from "../images/did_logo1.png";
import Profile from "../components/ProfileDetail/Profile";
import PaymentHistory from "../components/adminComponents/Billing/PaymentHistory";
import InvoiceHistory from "../components/adminComponents/Billing/InvoiceHistory";
import Dashboard from "../components/superadmin/Dashboard";
import Users from "../components/superadmin/Users";
import Campaigns from "../components/superadmin/Campaigns";
import Transactions from "../components/superadmin/Transactions";
import Numbers from "../components/superadmin/Numbers";
import KycDocuments from "../components/superadmin/KycDocuments";
import Inbound_Ip from "../components/superadmin/Inbound_Ip";
// const navigation = [ 
//   { name: "Dashboard", href: "/superadmin/dashboard", icon: ChartPieIcon, component: "Dashboard" },
//   { name: "Users", href: "/superadmin/users", icon: UsersIcon, component: "Users" },
//   { name: "Campaigns", href: "/superadmin/campaigns", icon: PhoneIcon, component: "Campaigns" },
//   { name: "Transactions", href: "/superadmin/transactions", icon: CurrencyDollarIcon, component: "Transactions" },
// ];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function SuperAdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // First define navigation items
  const defaultNavigation = [
    {
      name: "Dashboard",
      component: <Dashboard />,
      icon: ChartPieIcon,
      current: true,
    },
    {
      name: "Users",
      component: <Users />,
      icon: UsersIcon,
      current: false,
    },
    {
      name: "Numbers",
      component: <Numbers />,
      icon: PhoneIcon,
      current: false,
    },
    {
      name: "Kyc Documents",
      component: <KycDocuments />,
      icon: ClipboardIcon,
      current: false,
    },
    {
      name: "Inbound Ip",
      component: <Inbound_Ip />,
      icon: ClipboardIcon,
      current: false,
    },
  ];

  // Then set navigation state using defaultNavigation
  const [navigation, setNavigation] = useState(defaultNavigation);
  
  // Now set currentComponent using the defaultNavigation
  const [currentComponent, setCurrentComponent] = useState(defaultNavigation[0].component);

  const [teams, setTeams] = useState([
    {
      id: 1,
      name: "Transactions",
      component: <Transactions />,
      initial: "T",
      current: false,
    },
  ]);

  const [currentDateTime, setCurrentDateTime] = useState(
    new Date()
      .toLocaleString("en-GB", {
        timeZone: "GMT",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      })
      .replace(/(\d+)\/(\d+)\/(\d+), (\d+:\d+:\d+)/, "$3-$2-$1 $4 UTC")
  );

  useEffect(() => {
    const superadminToken = localStorage.getItem('superadminToken');
    const superadminUser = localStorage.getItem('superadminUser');
    
    if (!superadminToken || !superadminUser) {
      window.location.href = '/superadmin/login';
      return;
    }

    try {
      const user = JSON.parse(superadminUser);
      if (!user || user.role !== 'superadmin') {
        window.location.href = '/superadmin/login';
      }
    } catch (error) {
      console.error('Invalid user data:', error);
      window.location.href = '/superadmin/login';
    }
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(
        new Date()
          .toLocaleString("en-GB", {
            timeZone: "GMT",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
          })
          .replace(/(\d+)\/(\d+)\/(\d+), (\d+:\d+:\d+)/, "$3-$2-$1 $4 UTC")
      );
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Add error boundary for currentComponent rendering
  const renderCurrentComponent = () => {
    try {
      return currentComponent || defaultNavigation[0].component;
    } catch (error) {
      console.error('Error rendering component:', error);
      return <Dashboard />; // Fallback to Dashboard component
    }
  };

  return (
    <div className="mt-[-64px]">
      <Dialog
        open={sidebarOpen}
        onClose={setSidebarOpen}
        className="relative z-50 lg:hidden"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
        />

        <div className="fixed inset-0 flex">
          <DialogPanel
            transition
            className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
          >
            <TransitionChild>
              <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  className="-m-2.5 p-2.5"
                >
                  <span className="sr-only">Close sidebar</span>
                  <XMarkIcon className="size-6 text-white" aria-hidden="true" />
                </button>
              </div>
            </TransitionChild>
            {/* Mobile sidebar */}
            <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
              <div className="flex h-16 shrink-0 items-center">
                <Link to="/">
                  <img className="h-8 w-auto" src={logo} alt="DiDRoute" />
                </Link>
              </div>
              <nav className="flex flex-1 flex-col">
                <ul role="list" className="flex flex-1 flex-col gap-y-7">
                  <li>
                    <ul role="list" className="-mx-2">
                      {navigation.map((item) => (
                        <li key={item.name}>
                          <Link
                            to={item.href}
                            className={classNames(
                              item.component === currentComponent
                                ? "bg-gray-200 text-black"
                                : "text-gray-900",
                              "group flex gap-x-3 rounded-md p-2 text-sm font-medium"
                            )}
                            onClick={() => setCurrentComponent(item.component)}
                          >
                            <item.icon className="size-6 shrink-0" aria-hidden="true" />
                            {item.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                  <li className="mt-auto">
                    <a
                      href="#"
                      className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold text-gray-900 hover:bg-gray-50"
                    >
                      <Cog6ToothIcon className="size-6 shrink-0" aria-hidden="true" />
                      Help Center
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-48 lg:flex-col border-r border-gray-200">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto custom-scrollbar bg-white px-4 pb-4">
          <Link to="/" className="flex h-16 shrink-0 items-center">
            <img className="h-12 w-80" src={logo} alt="DiDRoute" />
          </Link>

          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" className="-mx-2">
                  <div className="text-gray-900 flex items-center font-semibold mb-2 px-2 pb-2 rounded-md shadow-sm">
                    <span className="text-xs">{currentDateTime}</span>
                  </div>

                  {navigation.map((item) => (
                    <li key={item.name}>
                      <a
                        onClick={() => {
                          setCurrentComponent(item.component);
                          setNavigation(navigation.map(nav => ({
                            ...nav,
                            current: nav.name === item.name
                          })));
                          setTeams(teams.map(t => ({
                            ...t,
                            current: false
                          })));
                        }}
                        className={classNames(
                          item.current
                            ? "bg-gray-200 text-black"
                            : "text-gray-900",
                          "group flex gap-x-3 rounded-md p-2 text-xs/5 font-medium cursor-pointer"
                        )}
                      >
                        <item.icon
                          className="size-4 shrink-0"
                          aria-hidden="true"
                        />
                        {item.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </li>
              <li>
                <div className="text-xs/6 font-semibold text-gray-800">
                  Billing
                </div>
                <ul role="list" className="-mx-2 mt-2 space-y-1">
                  {teams.map((team) => (
                    <li key={team.name}>
                      <a
                        href={team.href}
                        onClick={() => {
                          setCurrentComponent(team.component);
                          setTeams(teams.map(t => ({
                            ...t,
                            current: t.id === team.id
                          })));
                          setNavigation(navigation.map(nav => ({
                            ...nav,
                            current: false
                          })));
                        }}
                        className={classNames(
                          team.current
                            ? "bg-gray-200 text-black"
                            : "text-gray-900",
                          "group flex gap-x-3 rounded-md p-2 text-xs/5 font-medium"
                        )}
                      >
                        <span className="flex size-5 shrink-0 items-center justify-center rounded-lg border border-gray-700 bg-white text-[0.625rem] font-medium text-black group-hover:text-gray-800">
                          {team.initial}
                        </span>
                        <span className="truncate">{team.name}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </li>
              <li className="mt-auto">
                <a
                  href="#"
                  className="group -mx-2 flex gap-x-3 rounded-md p-2 text-xs/5 font-semibold text-black hover:bg-gray-300 hover:text-black"
                >
                  <Cog6ToothIcon className="size-5 shrink-0" aria-hidden="true" />
                  Help Center
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-48">
        <div className="sticky top-0 z-40 flex h-14 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="size-6" aria-hidden="true" />
          </button>

          <div className="h-6 w-px bg-gray-900/10 lg:hidden" aria-hidden="true" />

          <div className="flex flex-1 gap-x-3 self-stretch lg:gap-x-3">
            <form className="flex flex-1" />
            <div className="flex items-center gap-x-2 lg:gap-x-4">
              <button
                type="button"
                className="-m-2.5 p-2.5 text-gray-700 hover:text-gray-500"
              >
                <span className="sr-only">View notifications</span>
                <BellIcon className="size-5" aria-hidden="true" />
              </button>
              <div
                className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-900/10"
                aria-hidden="true"
              />
              <Profile isSuperAdmin={true} />
            </div>
          </div>
        </div>

        <main className="py-6 mt-[64px]">
          <div className="px-2 sm:px-2 lg:px-4">
            {renderCurrentComponent()}
          </div>
        </main>
      </div>
    </div>
  );
} 